import { useCallback, useMemo, useRef, useState } from 'react'
import Papa from 'papaparse'
import { AcceptedData } from 'export-to-csv/output/lib/types'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import {
  ProductionRequirementRowType,
  SheduleTableProps,
} from './Scheduler.types'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material'
import {
  GridPreProcessEditCellProps,
  GridRenderCellParams,
  GridRenderEditCellParams,
  useGridApiContext,
} from '@mui/x-data-grid-premium'
import type { GridApiPremium } from '@mui/x-data-grid-premium'
import { WEEK_NUMBERS } from './ProductRequirementsCell.view'
import {
  DownTimeMiniTimeline,
  DownTimeWindowsDialog,
} from './DownTimeWindowsEditor'
import { FONT_SANS, TOKENS } from './SchedulerDesign'
import {
  ProductRates,
  TimeWindowShortfall,
  asBool,
  shortWindowWarning,
  SPECIFIC_TIME_MODE_LABEL,
  SpecificTimeMode,
  specificTimeModeOf,
  normaliseSpecificTimeUploadRow,
  specificTimeDownloadColumns,
  uploadShortfallWarnings,
  timeWindowShortfall,
} from './specificTime'

// Carry-over line(s) as chips — same comma-separated multi-line shape and
// component pair as SchedulerSynchronizedLines.muiTable.tsx's LinesCell,
// adapted here to per-row (product-specific) options instead of one global
// list.
const CarryThroughLineCell = ({ value }: GridRenderCellParams) => {
  if (!value) return null
  const lines = String(value)
    .split(',')
    .map((l) => l.trim())
    .filter(Boolean)
  return (
    <Stack
      direction="row"
      spacing={0.5}
      flexWrap="wrap"
      useFlexGap
      sx={{ py: 0.5 }}
    >
      {lines.map((line) => (
        <Chip key={line} label={line} size="small" />
      ))}
    </Stack>
  )
}

const CarryThroughLineEditCell = ({
  id,
  value,
  field,
  row,
  routeOptions,
}: GridRenderEditCellParams & { routeOptions: RouteOptions }) => {
  const apiRef = useGridApiContext()
  const availableLines =
    routeOptions[(row as ProductionRequirementRowType)?.productCode]?.lines ??
    []

  const currentLines = value
    ? String(value)
        .split(',')
        .map((l) => l.trim())
        .filter(Boolean)
    : []

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string[]) => {
      const newValueStr = newValue.join(', ')
      apiRef.current.setEditCellValue({ id, field, value: newValueStr })
    },
    [apiRef, id, field]
  )

  return (
    <Autocomplete
      multiple
      value={currentLines}
      onChange={handleChange}
      options={availableLines}
      disableCloseOnSelect
      size="small"
      sx={{ width: '100%', minWidth: 200 }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder="Select lines..."
          autoFocus
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option}
            label={option}
            size="small"
          />
        ))
      }
    />
  )
}

// Both time-window columns are entered in hours from the start of the
// CONSOLIDATED HORIZON, not hours into the week whose tab is open. Week 2's
// Sunday is 336, week 3's is 504, and so on — which is the frame the engine
// reads them in (`preprocess::apply_week_splitting` propagates the per-week
// row's value onto that week's virtual product unchanged).
//
// The ceiling used to be 168 for both, which silently parsed a week-2 deadline
// of 336 back down to 168 the moment the cell was committed: the planner typed
// the only value that means anything for week 2 and the grid answered with the
// end of week 1. A schedule runs at most WEEK_NUMBERS.length weeks, so the
// horizon — and the ceiling — is that many weeks of hours.
const HOURS_PER_WEEK = 168
const MAX_HORIZON_HOURS = WEEK_NUMBERS.length * HOURS_PER_WEEK

// Hours are whole numbers everywhere in the schema, and a deadline the planner
// cannot express is worse than one they can overshoot, so the parser clamps to
// the horizon rather than to the open week.
const parseHorizonHour = (value: number) =>
  Math.min(MAX_HORIZON_HOURS, Math.max(0, Number(value) || 0))

// Read a sibling field's value from the live edit (otherFieldsProps) if the
// user is editing it in the same commit, otherwise fall back to the saved row.
const liveFieldValue = (
  params: GridPreProcessEditCellProps,
  field: string
): unknown => {
  const live = (
    params as GridPreProcessEditCellProps & {
      otherFieldsProps?: Record<string, { value?: unknown }>
    }
  ).otherFieldsProps?.[field]?.value
  if (live !== undefined) return live
  return (params.row as Record<string, unknown>)[field]
}

const dataConfig = {
  required: ['productCode', 'quantity'],
  optional: [
    'productName',
    'quantityMade',
    'quantityStillToMake',
    'dontStartBefore',
    'finishBefore',
    'finishBeforeHard',
    'specificTime',
    'specificTimeWindows',
    'specificTimeMode',
    'carryThroughQuantity',
    'carryThroughKitchen',
    'carryThroughLine',
    'carryThroughMaxRunConsumed',
  ],
}

/** Kitchens and packing lines a given product can actually run on, derived
 *  from the packing-routes table. Keyed by productCode. */
export type RouteOptions = Record<
  string,
  { kitchens: string[]; lines: string[] }
>

/** True when a carry-through volume has actually been entered. Mirrors the
 *  engine, which counts only a strictly positive volume as a carry-over
 *  (`preprocess.rs`, `parse_product_requirements`) — 0, '' and null are all
 *  the ordinary "no carry-through" row. */
const hasCarryOver = (v: unknown): boolean => {
  if (v === null || v === undefined || v === '') return false
  const n = Number(v)
  return Number.isFinite(n) && n > 0
}

/** A number the user has actually entered. Distinct from `hasCarryOver`
 *  because 0 is a legitimate answer for hours consumed but not for a volume. */
const isEnteredNumber = (v: unknown): boolean => {
  if (v === null || v === undefined || v === '') return false
  const n = Number(v)
  return Number.isFinite(n) && n >= 0
}

/** A carry-over row is incomplete without all three allocation fields, and
 *  preprocess rejects it outright (spec test 5). Flag the field that is
 *  missing rather than blocking the quantity that revealed it — see the
 *  `carryThroughQuantity` column's `preProcessEditCellProps`.
 *
 *  Returns the highlight class for a cell, or '' when there is nothing to say.
 */
const carryOverNeedsInput = (
  row: ProductionRequirementRowType | undefined,
  field:
    | 'carryThroughKitchen'
    | 'carryThroughLine'
    | 'carryThroughMaxRunConsumed'
): string => {
  if (!row || !hasCarryOver(row.carryThroughQuantity)) return ''
  const v = (row as unknown as Record<string, unknown>)[field]
  // Hours consumed is the odd one out: 0 is a real answer there, so it is
  // "entered" rather than merely truthy.
  const filled =
    field === 'carryThroughMaxRunConsumed'
      ? isEnteredNumber(v)
      : Boolean(v) && String(v).trim() !== ''
  return filled ? '' : 'sch-needs-input'
}

// Coerce a possibly-null/empty/string field to a finite number. Used to keep
// the recompute formula `still = quantity - made` numerically stable when
// the source row has nulls or pre-typed string values.
const toNumber = (v: unknown): number => {
  if (v === null || v === undefined || v === '') return 0
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
export const SchedulerProductionRequirementTable = ({
  data,
  isLoading,
  updateTableData,
  uploadScheduleData,
  isUploading,
  isSaving,
  updateRowsStatus,
  predicate,
  deleteTableData,
  isDeleting,
  unfilteredData,
  showRescheduleColumns = false,
  showCarryThroughColumns = false,
  routeOptions = {},
  showSpecificTime = true,
  productRates = {},
}: SheduleTableProps<ProductionRequirementRowType> & {
  /** Reveal the `quantityMade` / `quantityStillToMake` columns for mid-week
   *  reschedule. Driven by the Re-schedule toggle in the parent view. */
  showRescheduleColumns?: boolean
  /** Reveal the three carry-through columns. Driven by the Carry-over toggle
   *  in the parent view, which also forces them on when any row already
   *  carries a volume — hiding existing data would be worse than clutter. */
  showCarryThroughColumns?: boolean
  /** Per-product valid kitchens/lines from packing-routes, used to constrain
   *  the two allocation dropdowns. An empty map degrades to a free-text-less
   *  but unconstrained picker rather than blocking entry. */
  routeOptions?: RouteOptions
  /** Show the Specific Time column. Off on product master data: the windows
   *  are hours of one week, so they belong on a week's demand row. */
  showSpecificTime?: boolean
  /** Per-product line rates from packing-routes, for the "windows too short"
   *  check made when Specific Time windows are saved. */
  productRates?: ProductRates
}) => {
  // --- Specific Time in the CSV -------------------------------------------
  // The grid shows Specific Time as one tick column, so the windows and the
  // rule would never reach a download. They are written as two columns beside
  // the tick, named after their fields so a re-upload maps them on its own,
  // and the upload puts all three cells into the form the popup saves before
  // the file goes to the service (see `normaliseSpecificTimeUploadRow`).
  const expandDownloadRow = useCallback(
    (
      out: Record<string, AcceptedData>,
      row: Record<string, unknown>
    ): Record<string, AcceptedData> => {
      if (!('specificTime' in out)) return out
      const extra = specificTimeDownloadColumns(row)
      const res: Record<string, AcceptedData> = {}
      for (const [k, v] of Object.entries(out)) {
        res[k] = v
        if (k === 'specificTime') {
          res.specificTimeWindows = extra.specificTimeWindows
          res.specificTimeMode = extra.specificTimeMode
        }
      }
      return res
    },
    []
  )
  // An upload whose time windows are too short for their demand, held until
  // the planner says to send it anyway — the same question the popup and the
  // Don't Start Before / Finish Before edit ask (`uploadShortfallWarnings`).
  const [pendingUpload, setPendingUpload] = useState<{
    file: File
    warnings: string[]
  } | null>(null)
  const uploadWithSpecificTime = useCallback(
    (file: File) => {
      if (!showSpecificTime) {
        uploadScheduleData(file)
        return
      }
      Papa.parse<Record<string, unknown>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const rows = result.data.map(normaliseSpecificTimeUploadRow)
          const ready = new File([Papa.unparse(rows)], file.name, {
            type: 'text/csv',
          })
          const warnings = uploadShortfallWarnings(
            rows,
            (unfilteredData ?? data ?? []) as unknown as Record<string, unknown>[],
            productRates
          )
          if (warnings.length) setPendingUpload({ file: ready, warnings })
          else uploadScheduleData(ready)
        },
        // Never lose the upload over this step: send the file as it came.
        error: () => uploadScheduleData(file),
      })
    },
    [uploadScheduleData, showSpecificTime, unfilteredData, data, productRates]
  )

  // --- Specific Time ---------------------------------------------------------
  // Ticking the box opens the windows popup (the downtime/trial one, with no
  // window limit); saving writes the tick and the windows in one row update.
  // Windows too short for the demand on the fastest line ask for a confirm
  // first. Unticking keeps the windows, so re-ticking starts from them; the
  // engine ignores windows on an unticked row.
  const [editingSpecific, setEditingSpecific] =
    useState<ProductionRequirementRowType | null>(null)
  const [pendingSpecific, setPendingSpecific] = useState<{
    row: ProductionRequirementRowType
    warning: string
  } | null>(null)

  const saveSpecificTime = (
    row: ProductionRequirementRowType,
    windows: string,
    mode: SpecificTimeMode
  ) => {
    updateTableData({
      [row.id]: {
        ...row,
        specificTime: windows.trim() !== '',
        specificTimeWindows: windows,
        specificTimeMode: mode,
      },
    })
  }

  // --- Don't Start Before / Finish Before too close together -----------------
  // Checked on Save. A row whose window cannot hold its demand on the fastest
  // line asks for a confirm first; saved anyway, the run trims the demand to
  // what fits and reports the rest as unmet (the engine makes the same
  // estimate, `preprocess::trim_demand_to_time_window`). Only rows where the
  // times or the quantity were changed ask, so editing another column of an
  // already-short row does not ask again.
  const apiRef = useRef<GridApiPremium | null>(null)
  const [pendingWindows, setPendingWindows] = useState<{
    changed: Record<number, ProductionRequirementRowType>
    short: { row: ProductionRequirementRowType; gap: TimeWindowShortfall }[]
  } | null>(null)

  const WINDOW_FIELDS = [
    'quantity',
    'quantityStillToMake',
    'dontStartBefore',
    'finishBefore',
  ] as const

  const savedRow = (id: number) => (data ?? []).find((r) => r.id === id)

  // Handle data updates for BaseTable
  const handleDataUpdate = (
    changedRows: Record<number, ProductionRequirementRowType>
  ) => {
    const short = Object.values(changedRows).flatMap((row) => {
      const before = savedRow(row.id)
      const touched =
        !before ||
        WINDOW_FIELDS.some(
          (f) => String(before[f] ?? '') !== String(row[f] ?? '')
        )
      const gap = touched ? timeWindowShortfall(productRates, row) : null
      return gap ? [{ row, gap }] : []
    })
    if (short.length > 0) {
      setPendingWindows({ changed: changedRows, short })
      return
    }
    updateTableData(changedRows)
  }

  // "No, change them": save the other edits, and put the short rows back to
  // their saved values. BaseTable has already cleared its unsaved rows, so
  // without the revert the grid would show times that were never saved.
  const rejectShortWindows = () => {
    if (!pendingWindows) return
    const shortIds = new Set(pendingWindows.short.map((s) => s.row.id))
    const rest = Object.fromEntries(
      Object.entries(pendingWindows.changed).filter(
        ([, row]) => !shortIds.has(row.id)
      )
    )
    if (Object.keys(rest).length > 0) updateTableData(rest)
    const reverted = [...shortIds]
      .map((id) => savedRow(id))
      .filter((r): r is ProductionRequirementRowType => r != null)
    if (reverted.length > 0) apiRef.current?.updateRows(reverted)
    setPendingWindows(null)
  }

  // Fields that only appear when the user has flipped the Re-schedule toggle.
  // Hidden by default so the normal-flow view stays uncluttered.
  const RESCHEDULE_FIELDS = ['quantityMade', 'quantityStillToMake'] as const
  const CARRY_THROUGH_FIELDS = [
    'carryThroughQuantity',
    'carryThroughKitchen',
    'carryThroughLine',
    'carryThroughMaxRunConsumed',
  ] as const

  // Carry-over line is a comma-separated multi-value field (one carry-over
  // row can claim several lines at once — see CarryThroughLineEditCell).
  const splitLines = (v: unknown): string[] =>
    typeof v === 'string'
      ? v
          .split(',')
          .map((l) => l.trim())
          .filter(Boolean)
      : []

  // CT-2: at most one carry-through per line and one kitchen BATCH per
  // kitchen. Checked across the other rows rather than within the row being
  // edited, because the conflict is inherently between demand lines. The
  // engine rejects the second claimant and names it in a run warning; flagging
  // it here means the user sees it while typing instead of in a run log. For
  // the (possibly multi-valued) line field, a conflict is any overlap between
  // this row's lines and another row's lines — not strict equality. On the
  // kitchen, two products of the SAME base are one batch feeding two lines and
  // are not a conflict (engine v7.166.0); only a different base is.
  const claimConflict = (
    field: 'carryThroughKitchen' | 'carryThroughLine',
    value: unknown,
    rowId: number
  ): boolean => {
    if (!value) return false
    if (field === 'carryThroughLine') {
      const thisLines = splitLines(value)
      if (thisLines.length === 0) return false
      return (data ?? []).some(
        (other) =>
          other.id !== rowId &&
          hasCarryOver(other.carryThroughQuantity) &&
          splitLines(other.carryThroughLine).some((l) => thisLines.includes(l))
      )
    }
    const thisBase = (data ?? []).find((r) => r.id === rowId)?.inputProductCode
    return (data ?? []).some(
      (other) =>
        other.id !== rowId &&
        hasCarryOver(other.carryThroughQuantity) &&
        other[field] === value &&
        // Same base on the same kitchen is one batch, not a clash. An unknown
        // base on either side stays a clash, as before.
        !(
          thisBase &&
          other.inputProductCode &&
          String(thisBase) === String(other.inputProductCode)
        )
    )
  }

  // Stable, so BaseTable's effect hands the ref over once.
  const captureApiRef = useCallback((ref: React.RefObject<GridApiPremium>) => {
    apiRef.current = ref.current
  }, [])

  const columns: ExtendedGridColDef[] = useMemo(() => {
    const all: ExtendedGridColDef[] = [
      {
        headerName: 'Product code',
        field: 'productCode',
        type: 'string',
        flex: 1,
        cellClassName: 'sch-link',
      },
      {
        headerName: 'Product description',
        field: 'productName',
        type: 'string',
        flex: 1,
      },
      {
        headerName: 'Quantity',
        field: 'quantity',
        type: 'number',
        flex: 1,
        editable: true,
        cellClassName: 'sch-num',
        // Only a negative blocks the edit. 0 is a real answer: "nothing more
        // to make this week" — with a Carry-over set, the week's whole volume
        // is the carry-over (engine v7.180.14), and without one the engine
        // skips the row as it always has. This used to error on 0 too, and
        // MUI refuses to commit a cell in error, so the 0 was silently
        // discarded and the cell went back to its old value on Save (same
        // trap as the Carry-over column below; 31688578198, 2026-10-29).
        preProcessEditCellProps: (params) => {
          const val = params.props.value
          const n = Number(val)
          const negative =
            val !== null && val !== '' && Number.isFinite(n) && n < 0
          return { ...params.props, error: negative }
        },
        // Only recompute quantityStillToMake when the reschedule columns are
        // visible — i.e. the user is aware of and using the mid-week reschedule
        // feature. Without this guard, editing Quantity silently writes a
        // non-null quantityStillToMake based on a hidden quantityMade field,
        // which the scheduler then interprets as "already produced" and drops
        // the product entirely.
        valueSetter: (value, row) => {
          const q = toNumber(value)
          if (!showRescheduleColumns) {
            return { ...row, quantity: value as string }
          }
          const made = toNumber(row.quantityMade)
          return {
            ...row,
            quantity: value as string,
            quantityStillToMake: Math.max(0, q - made),
          }
        },
      },
      {
        headerName: 'Quantity made',
        field: 'quantityMade',
        type: 'number',
        flex: 1,
        editable: true,
        cellClassName: 'sch-num',
        // Default to 0 when the row hasn't been touched yet.
        valueGetter: (_value, row) => toNumber(row.quantityMade),
        // Recompute Quantity still to make so the user can drive the balance
        // by entering how much has been produced so far.
        valueSetter: (value, row) => {
          const made = toNumber(value)
          const q = toNumber(row.quantity)
          return {
            ...row,
            quantityMade: made,
            quantityStillToMake: Math.max(0, q - made),
          }
        },
      },
      {
        headerName: 'Quantity still to make',
        field: 'quantityStillToMake',
        type: 'number',
        flex: 1,
        editable: true,
        cellClassName: 'sch-num',
        // When neither this nor quantityMade has been set yet, fall back to
        // the original quantity (the normal-flow case).
        valueGetter: (_value, row) => {
          if (
            row.quantityStillToMake !== null &&
            row.quantityStillToMake !== undefined &&
            row.quantityStillToMake !== ''
          ) {
            return toNumber(row.quantityStillToMake)
          }
          return Math.max(
            0,
            toNumber(row.quantity) - toNumber(row.quantityMade)
          )
        },
        // Manual edits to "still to make" override the auto-recompute; we
        // don't push the change back into quantityMade (one-way binding).
        valueSetter: (value, row) => ({
          ...row,
          quantityStillToMake: toNumber(value),
        }),
      },
      {
        headerName: "Don't Start Before",
        field: 'dontStartBefore',
        type: 'number',
        flex: 1,
        editable: true,
        cellClassName: 'sch-num',
        description:
          'The earliest hour this product may be packed on a line. The kitchen may start its batch a little earlier, so the line is running on the hour, but never before the start of that week. Hours from the start of the whole horizon, not from the start of this week. Week 1 starts at 0, week 2 at 168, week 3 at 336 — so "not before week 3" is 336, whichever week tab you are on.',
        valueParser: parseHorizonHour,
        preProcessEditCellProps: (params) => {
          const val = params.props.value
          const finishBefore = liveFieldValue(params, 'finishBefore')
          const invalid =
            val !== null &&
            val !== '' &&
            finishBefore !== null &&
            finishBefore !== undefined &&
            finishBefore !== '' &&
            Number(finishBefore) <= Number(val)
          return { ...params.props, error: invalid }
        },
      },
      {
        headerName: 'Finish Before',
        field: 'finishBefore',
        type: 'number',
        flex: 1,
        editable: true,
        // Flag a window too short for the demand, which the run will trim.
        // Highlights rows that never went through the Save check too, such as
        // uploaded ones.
        cellClassName: ({ row }) =>
          timeWindowShortfall(productRates, row as ProductionRequirementRowType)
            ? 'sch-num sch-needs-input'
            : 'sch-num',
        description:
          'Hours from the start of the whole horizon, not from the start of this week. The end of week 1 is 168, week 2 is 336, week 3 is 504, up to 1344 for week 8. On its own this is a preference the scheduler pays a late penalty to break; tick Finish Before (Hard) to make it a deadline it cannot cross.',
        valueParser: parseHorizonHour,
        preProcessEditCellProps: (params) => {
          const val = params.props.value
          const dontStartBefore = liveFieldValue(params, 'dontStartBefore')
          const invalid =
            val !== null &&
            val !== '' &&
            dontStartBefore !== null &&
            dontStartBefore !== undefined &&
            dontStartBefore !== '' &&
            Number(val) <= Number(dontStartBefore)
          return { ...params.props, error: invalid }
        },
      },
      {
        headerName: 'Finish Before (Hard)',
        field: 'finishBeforeHard',
        type: 'boolean',
        flex: 1,
        editable: true,
        description:
          'When ticked, the Finish Before time is enforced as a hard constraint instead of a soft late penalty: the run is truncated at the deadline rather than allowed to overrun it. Use it to keep a week’s demand inside its own week — without it a multi-week solve is free to merge two weeks of the same product into one later block. Volume that cannot be made before the deadline reports as unmet demand, which is the intended answer to a deadline the plant cannot meet.',
      },
      {
        headerName: 'Specific Time',
        field: 'specificTime',
        type: 'boolean',
        width: 190,
        description:
          'Tick to choose when this product may be packed. A timeline opens where you mark one or more windows in this week and pick what they mean. "Start and finish within": the product is packed inside those windows and nowhere else, split across them if it has to be, and what does not fit is left as unmet demand. "Start within": its production must START inside a window and may carry on after the window closes. Either way the kitchen may start its batch shortly before the window so the line is ready when it opens. Click the timeline to change the windows or the type.',
        valueGetter: (value) => asBool(value),
        renderCell: (params) => {
          const row = params.row as ProductionRequirementRowType
          const ticked = asBool(row.specificTime)
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                width: '100%',
                height: '100%',
              }}
            >
              <Checkbox
                size="small"
                checked={ticked}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  if (e.target.checked) setEditingSpecific(row)
                  else
                    updateTableData({
                      [row.id]: { ...row, specificTime: false },
                    })
                }}
              />
              {ticked && (
                <Box
                  onClick={() => setEditingSpecific(row)}
                  title={SPECIFIC_TIME_MODE_LABEL[specificTimeModeOf(row)]}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {specificTimeModeOf(row) === 'start' && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 9.5,
                        fontWeight: 700,
                        color: TOKENS.brandTeal,
                        border: `1px solid ${TOKENS.brandTeal}`,
                        borderRadius: '2px',
                        px: '3px',
                        lineHeight: '14px',
                        flexShrink: 0,
                      }}
                    >
                      START
                    </Box>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <DownTimeMiniTimeline value={row.specificTimeWindows} />
                  </Box>
                </Box>
              )}
            </Box>
          )
        },
      },
      {
        headerName: 'Carry-over qty',
        field: 'carryThroughQuantity',
        type: 'number',
        flex: 1,
        editable: true,
        cellClassName: 'sch-num',
        description:
          'Volume of this product already running on the line at hour 0 of this week. It is fixed there and the rest of the week is scheduled around it. ADDED to Quantity, not part of it — enter Quantity as the volume still to make, and the week produces Quantity + Carry-over. Where more than one Carry-over line is selected this is the TOTAL across them: the scheduler divides it equally between the lines, so do not enter a per-line figure.',
        // Deliberately the plain shape every other editable numeric column
        // uses — Quantity above, Conversion Factor on product master data:
        // `type: 'number'` + `editable` and nothing else. This cell used to
        // carry a bespoke valueGetter/valueSetter pair to tolerate the
        // string-typed values Delta schema evolution can hand back
        // ("1200.0"). Unlike the reschedule columns' pairs just above —
        // which exist to RECOMPUTE `still = quantity - made` — this one only
        // re-typed a value, which belongs at the data boundary. That
        // coercion now happens once on the
        // way in — see `normalizeCarryOverNumerics` in
        // ScheduleProductionRequirement.view.tsx — so the grid never sees a
        // numeric column holding a string and this column needs no special
        // case of its own. (For the record, the pair was NOT what stopped an
        // edit sticking: MUI v8 calls `valueSetter` and hands the result
        // straight to `processRowUpdate` — useGridCellEditing.js:456.)
        //
        // Only the value's OWN validity blocks the edit — a negative volume,
        // which the engine rejects outright.
        //
        // It used to also error when the kitchen, line or max-run-consumed
        // fields were not yet filled in, on the reasoning that preprocess
        // rejects such a row anyway (spec test 5). That silently ate the
        // input: MUI refuses to commit a cell whose `preProcessEditCellProps`
        // returns `error: true`, so `processRowUpdate` never fired, the row
        // never reached `unsavedRows`, and Save sent nothing — the cell just
        // sat there looking raised. Worse, the grid is in `editMode="cell"`,
        // where `otherFieldsProps` is never populated, so `liveFieldValue`
        // could only ever see *committed* values: the quantity was
        // uncommittable until the other three had already been saved, which
        // is the opposite of the order anyone fills a row in. It read as
        // random — "sometimes it saves, sometimes the box stays proud".
        //
        // The incompleteness is still surfaced, on the fields that are
        // actually missing, by `carryOverNeedsInput` below — which highlights
        // without blocking.
        preProcessEditCellProps: (params) => {
          const val = params.props.value
          const n = Number(val)
          const negative =
            val !== null && val !== '' && Number.isFinite(n) && n < 0
          return { ...params.props, error: negative }
        },
      },
      {
        headerName: 'Carry-over kitchen',
        field: 'carryThroughKitchen',
        type: 'singleSelect',
        flex: 1,
        editable: true,
        cellClassName: ({ row }) =>
          carryOverNeedsInput(
            row as ProductionRequirementRowType,
            'carryThroughKitchen'
          ),
        description:
          'Kitchen the carry-over volume is running on. Mandatory when a carry-over quantity is set; at most one carry-over batch per kitchen — products of the same input product (base) carried over on different lines are one batch and are allowed.',
        valueOptions: ({ row }) =>
          routeOptions[(row as ProductionRequirementRowType)?.productCode]
            ?.kitchens ?? [],
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: claimConflict(
            'carryThroughKitchen',
            params.props.value,
            (params.row as ProductionRequirementRowType).id
          ),
        }),
      },
      {
        headerName: 'Carry-over line',
        field: 'carryThroughLine',
        type: 'string',
        flex: 1,
        editable: true,
        cellClassName: ({ row }) =>
          carryOverNeedsInput(
            row as ProductionRequirementRowType,
            'carryThroughLine'
          ),
        description:
          'Packing line(s) the carry-over volume is running on. Mandatory when a carry-over quantity is set; at most one carry-over per line. Select more than one when one kitchen batch is feeding several lines at once — the carry-over quantity is the TOTAL across them and is divided equally between the selected lines, since they shared one batch rather than each running all of it, so enter the whole volume, not the amount on one line.',
        renderCell: (params) => <CarryThroughLineCell {...params} />,
        renderEditCell: (params) => (
          <CarryThroughLineEditCell {...params} routeOptions={routeOptions} />
        ),
        preProcessEditCellProps: (params) => ({
          ...params.props,
          error: claimConflict(
            'carryThroughLine',
            params.props.value,
            (params.row as ProductionRequirementRowType).id
          ),
        }),
      },
      {
        headerName: 'Carry-over run hrs used',
        field: 'carryThroughMaxRunConsumed',
        type: 'number',
        flex: 1,
        editable: true,
        cellClassName: ({ row }) =>
          [
            'sch-num',
            carryOverNeedsInput(
              row as ProductionRequirementRowType,
              'carryThroughMaxRunConsumed'
            ),
          ]
            .filter(Boolean)
            .join(' '),
        description:
          'Hours the kitchen had already been running on this base material when this week started — history before hour 0, not counting any of the carry-over volume shown in this week. The run started before this week, so only you know this. If these hours plus the carry-over block would exceed the maximum run length, the carry-over is cut short at the limit, cleaned, and the volume left over is scheduled under the normal rules.',
        preProcessEditCellProps: (params) => {
          const val = params.props.value
          const n = Number(val)
          if (val !== null && val !== '' && Number.isFinite(n) && n < 0) {
            return { ...params.props, error: true }
          }
          // Required once a volume is present — same rule as the two
          // allocations, for the same reason: the engine cannot derive it.
          const qty = liveFieldValue(params, 'carryThroughQuantity')
          return {
            ...params.props,
            error: hasCarryOver(qty) && !isEnteredNumber(val),
          }
        },
      },
    ]
    const hidden = new Set<string>()
    if (!showRescheduleColumns) RESCHEDULE_FIELDS.forEach((f) => hidden.add(f))
    if (!showCarryThroughColumns)
      CARRY_THROUGH_FIELDS.forEach((f) => hidden.add(f))
    if (!showSpecificTime) hidden.add('specificTime')
    return all.filter((c) => !hidden.has(c.field))
  }, [
    data,
    showRescheduleColumns,
    showCarryThroughColumns,
    routeOptions,
    showSpecificTime,
    productRates,
  ])

  return (
    <Box id="#scheduler-production-requirement-table">
      <BaseTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleDataUpdate}
        uploadData={() => {}} // Upload handled by external DropZone above
        isUploading={false}
        isSaving={isSaving}
        apiRefCallback={captureApiRef}
        checkBoxSelection={true}
        showTableToolBar={true}
        downloadFileName="SchedulerProductionRequirement"
        expandDownloadRow={showSpecificTime ? expandDownloadRow : undefined}
        onDeleteRows={deleteTableData}
        isDeleting={isDeleting}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          '& .MuiBox-root': {
            padding: '0px !important',
          },
        }}
        multiStageUpload={{
          uploadFile: uploadWithSpecificTime,
          isUploading: isUploading,
          columns: columns,
          updateRowsStatus: updateRowsStatus,
          // Unfiltered on purpose — see `unfilteredData` in SheduleTableProps.
          originalData: unfilteredData ?? data,
          predicate: predicate,
          dataConfig: dataConfig,
        }}
      />
      <DownTimeWindowsDialog
        open={editingSpecific != null}
        title="Specific Time windows"
        resourceLabel={
          editingSpecific
            ? `${editingSpecific.productCode}${
                editingSpecific.productName
                  ? ` ${editingSpecific.productName}`
                  : ''
              }`
            : ''
        }
        initialValue={editingSpecific?.specificTimeWindows ?? ''}
        windowMode={
          editingSpecific ? specificTimeModeOf(editingSpecific) : 'within'
        }
        onClose={() => setEditingSpecific(null)}
        onSave={(value, _cleans, chosenMode) => {
          const row = editingSpecific
          setEditingSpecific(null)
          if (!row) return
          const mode = chosenMode ?? specificTimeModeOf(row)
          const warning =
            value.trim() === ''
              ? null
              : shortWindowWarning(productRates, row, value, mode)
          if (warning) {
            setPendingSpecific({
              row: {
                ...row,
                specificTimeWindows: value,
                specificTimeMode: mode,
              },
              warning,
            })
            return
          }
          saveSpecificTime(row, value, mode)
        }}
      />
      <Dialog
        open={pendingSpecific != null}
        onClose={() => setPendingSpecific(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: FONT_SANS, fontSize: 16 }}>
          Time window too short
        </DialogTitle>
        <DialogContent sx={{ fontFamily: FONT_SANS, fontSize: 13 }}>
          <Box>{pendingSpecific?.warning}</Box>
          <Box sx={{ mt: 1.5, fontWeight: 700 }}>
            Do you want to save these windows anyway?
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              // Back to the timeline with what was marked, to lengthen it.
              if (pendingSpecific) setEditingSpecific(pendingSpecific.row)
              setPendingSpecific(null)
            }}
            sx={{ textTransform: 'none' }}
          >
            No, change them
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingSpecific)
                saveSpecificTime(
                  pendingSpecific.row,
                  pendingSpecific.row.specificTimeWindows ?? '',
                  specificTimeModeOf(pendingSpecific.row)
                )
              setPendingSpecific(null)
            }}
            sx={{ textTransform: 'none' }}
          >
            Yes, save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={pendingUpload != null}
        onClose={() => setPendingUpload(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: FONT_SANS, fontSize: 16 }}>
          Not enough time to make the demand
        </DialogTitle>
        <DialogContent sx={{ fontFamily: FONT_SANS, fontSize: 13 }}>
          <Box sx={{ mb: 1.5 }}>
            {pendingUpload?.warnings.length === 1
              ? 'One row in this file has'
              : `${pendingUpload?.warnings.length ?? 0} rows in this file have`}{' '}
            a time window too short for its demand. What does not fit will be
            reported as unmet demand.
          </Box>
          {pendingUpload?.warnings.slice(0, 10).map((w, i) => (
            <Box key={i} sx={{ mb: 1 }}>
              {w}
            </Box>
          ))}
          {(pendingUpload?.warnings.length ?? 0) > 10 && (
            <Box sx={{ mb: 1 }}>
              …and {(pendingUpload?.warnings.length ?? 0) - 10} more.
            </Box>
          )}
          <Box sx={{ mt: 1.5, fontWeight: 700 }}>
            Do you want to upload the file anyway?
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingUpload(null)}
            sx={{ textTransform: 'none' }}
          >
            No, cancel the upload
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingUpload) uploadScheduleData(pendingUpload.file)
              setPendingUpload(null)
            }}
            sx={{ textTransform: 'none' }}
          >
            Upload anyway
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={pendingWindows != null}
        onClose={rejectShortWindows}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: FONT_SANS, fontSize: 16 }}>
          Not enough time to make the demand
        </DialogTitle>
        <DialogContent sx={{ fontFamily: FONT_SANS, fontSize: 13 }}>
          {pendingWindows?.short.map(({ row, gap }) => (
            <Box key={row.id} sx={{ mb: 1.5 }}>
              <Box sx={{ fontWeight: 700 }}>
                {row.productCode}
                {row.productName ? ` ${row.productName}` : ''}
              </Box>
              <Box>
                Required: {gap.neededHours}h to make{' '}
                {gap.quantity.toLocaleString()} on its fastest line, {gap.line}{' '}
                at {Math.round(gap.rate).toLocaleString()} an hour.
              </Box>
              <Box>
                Available: {gap.allowedHours}h, from Don&apos;t Start Before{' '}
                {gap.from}h to Finish Before {gap.to}h.
              </Box>
              <Box>
                If you continue, the demand is trimmed to{' '}
                {gap.keep.toLocaleString()} and the other{' '}
                {Math.max(0, gap.quantity - gap.keep).toLocaleString()} is
                reported as unmet demand.
              </Box>
            </Box>
          ))}
          <Box sx={{ mt: 1.5, fontWeight: 700 }}>
            Do you want to continue anyway?
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={rejectShortWindows} sx={{ textTransform: 'none' }}>
            No, change them
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingWindows) updateTableData(pendingWindows.changed)
              setPendingWindows(null)
            }}
            sx={{ textTransform: 'none' }}
          >
            Continue anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
